
import type { SfxPlayer } from "../audio/sfx";
import { installIntroStyle, introGround } from "./intro";

export interface Credentials {
  readonly username: string;
  readonly password: string;
}

export interface LoginOptions {
  readonly mount?: HTMLElement;
  /** Called with credentials, or with null and a guest name for guest play. */
  readonly onSubmit: (credentials: Credentials | null, name: string) => void;
  readonly onExit?: () => void;
  /** `loginok` on `dismiss`, `loginfail` on `fail`. Optional so a test
   *  fixture built without one stays silent. `main.ts` wires the shared
   *  `Sfx` here. */
  readonly sfx?: SfxPlayer;
  readonly onRegister?: (credentials: Credentials) => void;
  readonly onGesture?: () => void;
  /** Defaults to `guestRequested(location.search)`. */
  readonly guest?: boolean;
}

export const DEFAULT_GUEST_NAME = "Wanderer";

export function guestRequested(search: string): boolean {
  return new URLSearchParams(search).get("guest") === "1";
}

export const INPUT_LIMIT = 16;

/** `accounts.USERNAME_MIN` / `USERNAME_MAX` -- `server/accounts.py:89-90`. */
export const USERNAME_MIN = 3;
export const USERNAME_MAX = 16;
/** `accounts.PASSWORD_MIN` -- `server/accounts.py:91`. */
export const PASSWORD_MIN = 4;

export const LOGIN_TEXT = {
  /** LOGIN_CONNECT -- `locale_en/locale/en/locale_interface.txt:179`. */
  connect: "Connect",
  play: "Play as guest",
  entering: "Entering…",
  connecting: "You will be connected to the Server",
  inputId: "Enter the ID.",
  inputPassword: "Enter the password.",
  making: "Making it…",
  makeAccount: "Make account",
  failureUnknown: "You could not register for unknown reasons.",
} as const;

export function normalise(value: string): string {
  return value.trim();
}

export function guestName(value: string): string {
  const name = normalise(value);
  return name.length > 0 ? name : DEFAULT_GUEST_NAME;
}

export function rejectionMessage(code: number, reason: string): string {
  if (reason.trim().length > 0) return reason;
  return code === 0 ? LOGIN_TEXT.failureUnknown : `${LOGIN_TEXT.failureUnknown} ${code}`;
}

export function shouldReloadAfterRejection(joined: boolean): boolean {
  return joined;
}

export function registrationProblem(
  username: string,
  password: string,
  again: string,
): string | null {
  const name = normalise(username).toLowerCase();
  if (name.length < USERNAME_MIN) {
    return `Username must be at least ${USERNAME_MIN} characters.`;
  }
  if (name.length > USERNAME_MAX) {
    return `Username must be at most ${USERNAME_MAX} characters.`;
  }
  if (!/^[a-z0-9_]+$/.test(name)) {
    return "Username may contain only lowercase letters, digits and underscore.";
  }
  if (password.length < PASSWORD_MIN) {
    return `Password must be at least ${PASSWORD_MIN} characters.`;
  }
  // Checked here and nowhere else, because the server never sees the second
  // box: a confirmation field exists to catch a typo in a value nobody can read
  // back, and sending it would be sending the password twice for no gain. The
  // original has no such field -- it has no registration form at all.
  if (password !== again) return "The two passwords do not match.";
  return null;
}

export class LoginScreen {
  private readonly root: HTMLElement;
  private readonly form: HTMLFormElement;
  private readonly username: HTMLInputElement;
  private readonly password: HTMLInputElement;
  private readonly name: HTMLInputElement;
  private readonly error: HTMLElement;
  private readonly status: HTMLElement;
  private readonly submit: HTMLButtonElement;
  private readonly guest: HTMLButtonElement;
  private readonly guestForm: HTMLFormElement;
  private readonly register: HTMLFormElement;
  private readonly registerToggle: HTMLButtonElement;
  private readonly registerName: HTMLInputElement;
  private readonly registerPassword: HTMLInputElement;
  private readonly registerAgain: HTMLInputElement;
  private readonly registerButton: HTMLButtonElement;
  private readonly registerNote: HTMLElement;
  private readonly options: LoginOptions;
  private readonly guestBlock: HTMLElement;
  private gestured = false;

  constructor(options: LoginOptions) {
    this.options = options;
    const mount = options.mount ?? document.body;
    const doc = mount.ownerDocument;

    installIntroStyle(doc);

    const { ground, anchor } = introGround(doc, "login");
    this.root = ground;
    this.root.classList.add("login");
    anchor.innerHTML = TEMPLATE;
    mount.appendChild(this.root);

    this.form = this.must<HTMLFormElement>(".login-form");
    this.username = this.must<HTMLInputElement>('[name="username"]');
    this.password = this.must<HTMLInputElement>('[name="password"]');
    this.name = this.must<HTMLInputElement>('[name="guest-name"]');
    this.error = this.must<HTMLElement>(".login-error");
    this.status = this.must<HTMLElement>(".login-status");
    this.submit = this.must<HTMLButtonElement>(".login-signin");
    this.guest = this.must<HTMLButtonElement>(".login-play");
    this.guestForm = this.must<HTMLFormElement>(".login-guest");
    this.register = this.must<HTMLFormElement>(".login-register");
    this.registerToggle = this.must<HTMLButtonElement>(".login-register-toggle");
    this.registerName = this.must<HTMLInputElement>('[name="new-username"]');
    this.registerPassword = this.must<HTMLInputElement>('[name="new-password"]');
    this.registerAgain = this.must<HTMLInputElement>('[name="new-password-again"]');
    this.registerButton = this.must<HTMLButtonElement>(".login-register-go");
    this.registerNote = this.must<HTMLElement>(".login-register-note");
    this.guestBlock = this.must<HTMLElement>(".login-guest-block");

    // Guest play is not a control this screen offers by default; it is behind
    // `?guest=1` (see `guestRequested`). Otherwise the whole block leaves the
    // DOM, so the entry screen is account + password + Connect and nothing the
    // original never had.
    const guest =
      options.guest ??
      guestRequested(doc.defaultView?.location?.search ?? "");
    if (!guest) this.guestBlock.remove();

    // ------------------------------------------------------------- sign in
    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.gesture();
      // `__OnClickLoginButton` refuses the two boxes separately and returns
      // focus to whichever one is empty (`intrologin.py:1338-1349`): an empty
      // ID is LOGIN_INPUT_ID and an empty password is LOGIN_INPUT_PASSWORD,
      // never one merged sentence.
      if (normalise(this.username.value).length === 0) {
        this.showError(LOGIN_TEXT.inputId);
        this.username.focus();
        return;
      }
      if (this.password.value.length === 0) {
        this.showError(LOGIN_TEXT.inputPassword);
        this.password.focus();
        return;
      }
      this.busy(true);
      options.onSubmit(
        {
          username: normalise(this.username.value),
          password: this.password.value,
        },
        "",
      );
    });

    // The legacy LoginWindow advances from ID to password on both Return and
    // Tab (`intrologin.py:566-567`); the browser's native Tab order already
    // provides the latter, but Enter would otherwise submit an incomplete form
    // instead of moving focus as the original does.
    this.username.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      this.password.focus();
    });

    // ------------------------------------------------------------ register
    this.registerToggle.addEventListener("click", () => {
      this.gesture();
      this.register.hidden = !this.register.hidden;
      this.registerToggle.setAttribute("aria-expanded", String(!this.register.hidden));
      this.signingUp(!this.register.hidden);
      if (!this.register.hidden) this.registerName.focus();
    });

    // A refusal describes the values that just went over the wire. Clear it as
    // soon as the player edits any registration field, so the corrected form is
    // not still painted as refused before it has been sent again.
    for (const box of [this.registerName, this.registerPassword, this.registerAgain]) {
      box.addEventListener("input", () => this.clearRegisterNote());
    }

    // A real form rather than a click-only button: Enter in the repeated-
    // password box is the ordinary way a person finishes registration, and it
    // is the same key `intrologin.py:569` binds on the password edit line. The
    // one handler serves Enter and the button so validation cannot drift.
    const makeAccount = (): void => {
      this.gesture();
      const problem = registrationProblem(
        this.registerName.value,
        this.registerPassword.value,
        this.registerAgain.value,
      );
      if (problem !== null) {
        this.showRegisterNote(problem);
        return;
      }
      this.registerButton.disabled = true;
      this.registerButton.textContent = LOGIN_TEXT.making;
      options.onRegister?.({
        username: normalise(this.registerName.value),
        password: this.registerPassword.value,
      });
    };
    this.registerButton.addEventListener("click", makeAccount);
    this.register.addEventListener("submit", (event) => {
      event.preventDefault();
      makeAccount();
    });

    const wander = (): void => {
      this.gesture();
      this.busy(true);
      options.onSubmit(null, guestName(this.name.value));
    };
    this.guest.addEventListener("click", wander);
    this.guestForm.addEventListener("submit", (event) => {
      event.preventDefault();
      wander();
    });

    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      this.gesture();
      options.onExit?.();
    });

    // KeyboardInput is already listening and preventDefault()s WASD. Without
    // this, anything typed here containing those letters arrives truncated --
    // `password` is typed and `pssword` is sent, and the login fails with the
    // server's deliberately uninformative "incorrect username or password".
    for (const box of [
      this.username,
      this.password,
      this.name,
      this.registerName,
      this.registerPassword,
      this.registerAgain,
    ]) {
      for (const event of ["keydown", "keyup", "keypress"]) {
        box.addEventListener(event, (raw) => {
          if ((raw as KeyboardEvent).key !== "Escape") raw.stopPropagation();
        });
      }
    }

    this.root.addEventListener("pointerdown", () => this.gesture());
    this.username.focus();
  }

  private must<T extends Element>(selector: string): T {
    const found = this.root.querySelector<T>(selector);
    if (found === null) throw new Error(`login template is missing ${selector}`);
    return found;
  }

  private gesture(): void {
    if (this.gestured) return;
    this.gestured = true;
    this.options.onGesture?.();
  }

  private signingUp(value: boolean): void {
    this.root.classList.toggle("login-signing-up", value);
  }

  fail(code: number, reason: string): void {
    this.busy(false);
    // `OnConnectFailure`/`OnLoginFailure` (`intrologin.py:419,466`): this is
    // the one path both refusals reach here, a lost socket and a server
    // refusal alike.
    this.options.sfx?.play("loginfail");
    this.showError(rejectionMessage(code, reason));
    this.password.focus();
    this.password.select();
  }

  registered(ok: boolean, username: string, text: string): void {
    this.registerButton.disabled = false;
    this.registerButton.textContent = LOGIN_TEXT.makeAccount;
    this.showRegisterNote(text || (ok ? "Account made." : "Could not make it."), ok);
    if (!ok) return;
    this.registerPassword.value = "";
    this.registerAgain.value = "";
    this.username.value = username;
    this.password.value = "";
    this.password.focus();
  }

  dismiss(): void {
    // `OnHandShake` (`intrologin.py:434`): the socket handshake succeeding,
    // not character selection. `main.ts` only ever calls `dismiss` on a
    // forward path -- credentials accepted, an empire prompt, or the world
    // joined -- never on a refusal, which is `fail`'s own call.
    this.options.sfx?.play("loginok");
    this.root.remove();
  }

  private busy(value: boolean): void {
    this.submit.disabled = value;
    this.guest.disabled = value;
    // `intrologin.py:686-687` never rewrites `LoginButton`; it opens a popup on
    // the connecting string and leaves the board's own word alone, so the
    // sign-in button keeps saying Connect. The guest button is this build's
    // own control with no original to match, and says where it is going.
    this.guest.textContent = value ? LOGIN_TEXT.entering : LOGIN_TEXT.play;
    if (value) {
      this.clearError();
      this.showStatus(LOGIN_TEXT.connecting);
    } else {
      this.clearStatus();
    }
  }

  private showStatus(text: string): void {
    this.status.textContent = text;
    this.status.hidden = false;
  }

  private clearStatus(): void {
    this.status.textContent = "";
    this.status.hidden = true;
  }

  private showError(text: string): void {
    this.error.textContent = text;
    this.error.hidden = false;
  }

  private clearError(): void {
    this.error.textContent = "";
    this.error.hidden = true;
  }

  private showRegisterNote(text: string, good = false): void {
    this.registerNote.textContent = text;
    this.registerNote.dataset.tone = good ? "good" : "bad";
    this.registerNote.hidden = false;
  }

  private clearRegisterNote(): void {
    this.registerNote.textContent = "";
    this.registerNote.hidden = true;
  }
}

const TEMPLATE = `
  <main class="login-card" aria-labelledby="login-heading">
    <header class="login-head">
      <h1 id="login-heading" class="login-title">Metin3</h1>
      <p class="login-lede">Sign in and the world remembers you.</p>
    </header>

    <form class="login-form" autocomplete="off">
      <label class="login-field">
        <span class="login-field-label">Account</span>
        <input class="login-name login-account" name="username" type="text"
          aria-label="Account name" autocapitalize="off" autocorrect="off"
          spellcheck="false" maxlength="${INPUT_LIMIT}" autocomplete="username" value="">
      </label>
      <label class="login-field">
        <span class="login-field-label">Password</span>
        <input class="login-name login-secret" name="password" type="password"
          aria-label="Password" maxlength="${INPUT_LIMIT}"
          autocomplete="current-password" value="">
      </label>
      <button type="submit" class="login-signin">${LOGIN_TEXT.connect}</button>
      <div class="login-message">
        <p class="login-error" role="alert" aria-live="polite" hidden></p>
        <p class="login-status" role="status" aria-live="polite" hidden></p>
      </div>
    </form>

    <section class="login-register-block">
      <button type="button" class="login-register-toggle"
        aria-controls="login-register-form" aria-expanded="false"><span
        class="login-toggle-text">No account? Make one</span><span
        class="login-toggle-mark" aria-hidden="true"></span></button>
      <form id="login-register-form" class="login-register" hidden autocomplete="off">
        <label class="login-field">
          <span class="login-field-label">New account</span>
          <input class="login-name" name="new-username" type="text"
            aria-label="New account name" autocapitalize="off" autocorrect="off"
            spellcheck="false" maxlength="${INPUT_LIMIT}" autocomplete="off" value="">
        </label>
        <div class="login-pair">
          <label class="login-field">
            <span class="login-field-label">New password</span>
            <input class="login-name" name="new-password" type="password"
              aria-label="New password" maxlength="${INPUT_LIMIT}"
              autocomplete="new-password" value="">
          </label>
          <label class="login-field">
            <span class="login-field-label">Again</span>
            <input class="login-name" name="new-password-again" type="password"
              aria-label="Repeat the new password" maxlength="${INPUT_LIMIT}"
              autocomplete="new-password" value="">
          </label>
        </div>
        <div class="login-message">
          <p class="login-register-note" role="status" aria-live="polite" hidden></p>
        </div>
        <button type="button" class="login-register-go">${LOGIN_TEXT.makeAccount}</button>
      </form>
    </section>

    <section class="login-guest-block">
      <p class="login-guest-lede">Or look around without an account. A guest keeps
        nothing: no bag, no skills, no storage once the socket closes.</p>
      <form class="login-guest" autocomplete="off">
        <label class="login-field">
          <span class="login-field-label">Guest name</span>
          <input class="login-name" name="guest-name" type="text"
            aria-label="Guest name" placeholder="Leave blank to wander"
            autocapitalize="off" autocorrect="off" spellcheck="false"
            maxlength="16" autocomplete="nickname" value="">
        </label>
        <button type="button" class="login-play">${LOGIN_TEXT.play}</button>
      </form>
    </section>
  </main>
`;
